import {ChecklistDto, TripDto, ParticipantDto, ChecklistCategoryDto} from "../../api/generated";
import i18nextko from "../../components/util/i18nko";
import {createConfirmModal} from "../../components/elements/modal/modal";
import {Router} from "@profiscience/knockout-contrib-router";
import {myTripParticipantsApi} from "../../api/api-wrapper";
import {postbox} from "../../components/util/postbox";
import globalState from "../../global-state";

/**
 * Check if a trip is a shared trip.
 * @param trip
 */
export function isSharedTrip(trip: TripDto): boolean {
    return trip && trip.shareId && trip.shareId.trim() !== '';
}

/**
 * Check if this category has an owner.
 * @param category
 */
function hasCategoryOwner(category: ChecklistCategoryDto) {
    return category.owner && category.owner != null;
}

/**
 * Check if a category is shared.
 * It's shared if the trip is shared and the category does not have an owner.
 * @param trip
 * @param category
 */
export function isSharedCategory(trip: TripDto, category: ChecklistCategoryDto): boolean {
    return isSharedTrip(trip) && !hasCategoryOwner(category);
}

/**
 * Check if a category is private.
 * It's shared if the trip is shared and the category has an owner.
 * @param trip
 * @param category
 */
export function isPrivateCategory(trip: TripDto, category: ChecklistCategoryDto): boolean {
    return isSharedTrip(trip) && hasCategoryOwner(category);
}

/**
 * Check whether all categories are shared.
 * All categories are shared if none of them has a participant assigned.
 */
export function isAllCategoriesShared(categories: ChecklistCategoryDto[]): boolean {
    return typeof categories.find(
        category => hasCategoryOwner(category)) === 'undefined';
}

/**
 * Get the participantName.
 * @param participant
 */
export function participantName(participant: ParticipantDto) {
    const firstname = participant.firstname ? participant.firstname + ' ' : '';
    const lastname = participant.lastname ? participant.lastname : '';
    return firstname + lastname;
}

/**
 * Get the initials for an participant.
 * @param participant
 */
export function participantInitials(participant: ParticipantDto) {
    const firstname = participant.firstname ? participant.firstname.charAt(0) : '';
    const lastname = participant.lastname ? participant.lastname.charAt(0) : '';
    return firstname + lastname;
}

/**
 * Get the color for the participant icon.
 * @param idx
 */
export function participantIconColor(idx: number): string {
    // TODO move to config?
    const colors = ['#88BBE4', '#87DCC0', '#CDF1AF', '#E494D3', '#998AD3', '#F28997', '#F9C8A0', '#9B94BE', '#C5C2DF','#FFDEDA', '#FDB4C1'];
    while (idx > (colors.length - 1)) {
        idx = idx - colors.length;
    }
    return colors[idx];
}

/**
 * Show the participate overlay.
 */
export function showParticipateOverlay(trip: TripDto, checklist: ChecklistDto):Promise<any> {
    const title = i18nextko.t("share.participate.confirmTitle");
    const text = isAllCategoriesShared(checklist.categories) ?
        i18nextko.t("share.participate.confirmTextShareAll") : i18nextko.t("share.participate.confirmTextShareOne");
    const confirm = i18nextko.t("share.participate.join");
    const cancel = i18nextko.t("global.cancel");
    const iconCssClass = "fo-icon fo-icon-people";

    return createConfirmModal(text, title, confirm, cancel, iconCssClass)
        .then(() => {
            myTripParticipantsApi.addTripParticipant(String(trip.id), {})
                .then(() => {
                    // Add or update trip in global state.
                    const tripIdx = globalState.trips().findIndex(globalTrip => globalTrip.id === trip.id);
                    if(tripIdx == -1) {
                        globalState.trips().unshift(trip);
                    }

                    // forward to checklist view
                    Router.update(`/reise/${trip.id}/checkliste`, {
                        force: true,
                        push: false
                    });
                }).catch(response =>  {
                    // User already in participant list -> forward to checklist view
                    if(response.status && response.status === 409) {
                        Router.update(`/reise/${trip.id}/checkliste`, {
                            force: true,
                            push: false
                        });
                    } else {
                        // unexpected error
                        postbox.addError('share.create.errors.participate');
                        return Router.update('/', {
                            force: true,
                            push: true
                        })
                    }
                });
        })
        .catch(() => {
            // route home
            return Router.update('/', {
                force: true,
                push: true
            })
        });
}


